import { FaceLivenessDetector as AmplifyFaceLivenessDetector } from "@aws-amplify/ui-react-liveness"
import { useRouter } from "next/navigation.js"
import { useCallback, useContext, useState } from "react"
import ConfigurationContext from "../context/ConfigurationContext.js"
import RouterContext from "../context/RouterContext.js"
import Environment from "../domain/Environment.js"
import Translation from "../i18n/Translation.js"
import type { UUIDv4 } from "../schemata/shared/UUIDv4.js"
import type { FaceLivenessDetectionError } from "../support/error/FaceLivenessDetectionError.js"
import UnknownFaceLivenessDetectionError from "../support/error/UnknownFaceLivenessDetectionError.js"
import HTTPClient from "../support/http/HTTPClient.js"
import HTTPHeader from "../support/http/HTTPHeader.js"
import HTTPMethod from "../support/http/HTTPMethod.js"
import HTTPRequestBuilder from "../support/http/HTTPRequestBuilder.js"
import MIMEType from "../support/http/MIMEType.js"
import FaceLivenessDetectorErrorView from "./FaceLivenessDetectorErrorView.js"

type Properties = Readonly<{ sessionId: UUIDv4 }>

export default function FaceLivenessDetector({ sessionId }: Properties) {
	const [error, setError] = useState<FaceLivenessDetectionError>(UnknownFaceLivenessDetectionError)
	const configuration = useContext(ConfigurationContext)
	const router = useRouter()
	const uri = useContext(RouterContext)
	const [isAnalysisSuccessful, setIsAnalysisSuccessful] = useState<boolean | undefined>(undefined)

	const ErrorView = useCallback(() => {
		return <FaceLivenessDetectorErrorView faceLivenessDetectionError={error} source="amplify" />
	}, [configuration, error])

	const onAnalysisComplete = async (): Promise<void> => {
		if (uri) {
			const httpClient = new HTTPClient({
				insecureTLS: configuration.ENVIRONMENT === Environment.Local,
				keepAlive: false,
				proxy: _ => _
			})
			const response = await httpClient.sendAsync<{ uri: string }, { isLive: boolean; redirectUri: string }>(
				new HTTPRequestBuilder<{ uri: string }>()
					.body({ uri: uri.original.href })
					.header(HTTPHeader.ContentType, MIMEType.JSON)
					.method(HTTPMethod.PUT)
					.timeout(configuration.HTTP_CLIENT_TIMEOUT_IN_MILLISECONDS)
					.uri(new URL(`${configuration.BIOMETRY_ORIGIN}/face-liveness-detection`))
					.build()
			)
			if (response.body) {
				router.push(response.body.redirectUri)
			} else {
				setIsAnalysisSuccessful(false)
			}
		}
	}

	const onUserCancel = (): void => {}

	if (isAnalysisSuccessful === false) {
		return <FaceLivenessDetectorErrorView faceLivenessDetectionError={error} source="manual" />
	}
	return (
		<>
			<AmplifyFaceLivenessDetector
				components={{
					ErrorView,
					PhotosensitiveWarning: undefined
				}}
				disableStartScreen={true}
				displayText={Translation[configuration.DEFAULT_LOCALE]}
				onAnalysisComplete={onAnalysisComplete}
				onError={setError}
				onUserCancel={onUserCancel}
				region={configuration.AWS_REGION}
				sessionId={sessionId}
			/>
		</>
	)
}
